import React, { useRef, useMemo, useCallback } from 'react';
import type { FC } from 'react';
import { css } from '@compiled/react';
import { useVirtualizer } from '@tanstack/react-virtual';

import type { Node } from '@atlaskit/editor-prosemirror/model';
import { defaultNodeComponents } from '@atlaskit/renderer';

const parentStyles = css({
	height: '100vh',
	width: '100%',
	overflowY: 'auto',
	contain: 'strict',
});

const containerStyles = css({
	width: '100%',
	position: 'relative',
});

const UNSUPPORTED_CHILD_NODE_TYPES = [
	'heading',
	'embedCard',
	'extension',
	'extensionFrame',
	'bodiedExtension',
	'multiBodiedExtension',
	'inlineExtension',
];

type TableComponentProps = React.ComponentProps<typeof defaultNodeComponents.table>;

type VirtualizedTableComponentProps = Partial<TableComponentProps> & {
	children?: React.ReactNode;
	parentInfo?: { path: Node[] };
	childInfo?: { nodeTypeCounts: { [nodeType: string]: number } };
};

export const VirtualizedTableComponent: FC<VirtualizedTableComponentProps> = ({
	children,
	parentInfo,
	childInfo,
	...otherTableProps
}) => {
	const parentRef = useRef<HTMLDivElement>(null);

	const childrenArray = useMemo(() => React.Children.toArray(children), [children]);

	const getScrollElement = useCallback(() => parentRef.current, []);

	const virtualizer = useVirtualizer({
		count: childrenArray.length,
		getScrollElement,
		estimateSize: () => 45,
		overscan: 5,
	});

	const virtualItems = virtualizer.getVirtualItems();

	const TableComponent = useMemo(() => defaultNodeComponents.table, []);

	const isVirtualizationEnabled =
		// Virtualization is disabled for non-top level tables.
		(parentInfo?.path?.length || 0) < 2 &&
		// Virtualization is disabled if any unsupported child node types are present.
		!UNSUPPORTED_CHILD_NODE_TYPES.some((nodeType) => childInfo?.nodeTypeCounts[nodeType]);
	if (!isVirtualizationEnabled) {
		return <TableComponent {...otherTableProps}>{children}</TableComponent>;
	}

	return (
		<div ref={parentRef} data-testid="virtual-table-parent-container" css={parentStyles}>
			<div
				data-testid="virtual-table-container"
				style={{
					height: `${virtualizer.getTotalSize()}px`,
				}}
				css={containerStyles}
			>
				<TableComponent
					isNumberColumnEnabled={false} // Number column needs separate implementation
					{...otherTableProps}
				>
					{virtualItems.map((virtualRow) => {
						return childrenArray[virtualRow.index];
					})}
				</TableComponent>
			</div>
		</div>
	);
};
